/**
 * Line Four — pure local 1v1 game engine.
 *
 * Seven rollout bands act as the seven columns of a 7 x 6 board. A legal
 * putt drops a piece into the lowest open row in the matching column. Putt
 * short of 15 ft, beyond 50 ft, or into a full column and the turn is spent.
 * The first horizontal, vertical, or diagonal line of four wins.
 *
 * Distance boundaries are deliberately exact and shared by the rules and UI:
 * [15,20), [20,25), … [40,45), [45,50]. Thus a displayed/measured 20.0 ft
 * putt belongs to B, and 50.0 ft is still inside G.
 */

export type LineFourPlayer = 0 | 1;

export const LINE_FOUR_ROWS = 6;
export const LINE_FOUR_COLUMNS = 7;
export const LINE_FOUR_MIN_FT = 15;
export const LINE_FOUR_MAX_FT = 50;
export const LINE_FOUR_BAND_FT = 5;
export const LINE_FOUR_COLUMN_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;

export type LineFourColumnLabel = (typeof LINE_FOUR_COLUMN_LABELS)[number];

export interface LineFourCell {
  row: number;
  column: number;
}

export interface LineFourBand {
  column: number;
  label: LineFourColumnLabel;
  fromFt: number;
  toFt: number;
  /** All bands except G exclude their upper edge. G includes 50 ft. */
  includesUpperEdge: boolean;
}

/** Stable band catalogue used by both the green labels and the HUD board. */
export const LINE_FOUR_BANDS: readonly LineFourBand[] = LINE_FOUR_COLUMN_LABELS.map(
  (label, column) => ({
    column,
    label,
    fromFt: LINE_FOUR_MIN_FT + column * LINE_FOUR_BAND_FT,
    toFt: LINE_FOUR_MIN_FT + (column + 1) * LINE_FOUR_BAND_FT,
    includesUpperEdge: column === LINE_FOUR_COLUMNS - 1,
  })
);

export type LineFourBoard = Array<Array<LineFourPlayer | null>>;

export interface LineFourState {
  /** Row 0 is the BOTTOM of the board. Access as board[row][column]. */
  board: LineFourBoard;
  turnIndex: LineFourPlayer;
  firstPlayerIndex: LineFourPlayer;
  /** Legal pieces on the board; misses and full-column attempts do not add. */
  moveCount: number;
  /** Every resolved putt, including misses and attempts at full columns. */
  attemptCount: number;
  winnerIndex: LineFourPlayer | null;
  /** Every cell in the winning line(s), for an unambiguous HUD highlight. */
  winningCells: LineFourCell[];
  isDraw: boolean;
}

export type LineFourPuttVerdict =
  | {
      kind: 'placed';
      playerIndex: LineFourPlayer;
      column: number;
      row: number;
      won: boolean;
    }
  | { kind: 'miss'; playerIndex: LineFourPlayer }
  | { kind: 'full'; playerIndex: LineFourPlayer; column: number }
  | { kind: 'ignored' };

export interface AppliedLineFourPutt {
  state: LineFourState;
  verdict: LineFourPuttVerdict;
}

export function createLineFourBoard(): LineFourBoard {
  return Array.from({ length: LINE_FOUR_ROWS }, () =>
    Array<LineFourPlayer | null>(LINE_FOUR_COLUMNS).fill(null)
  );
}

export function createLineFourGame(firstPlayerIndex: LineFourPlayer = 0): LineFourState {
  return {
    board: createLineFourBoard(),
    turnIndex: firstPlayerIndex,
    firstPlayerIndex,
    moveCount: 0,
    attemptCount: 0,
    winnerIndex: null,
    winningCells: [],
    isDraw: false,
  };
}

/** Return the board column for a measured rollout, or null for a miss. */
export function lineFourColumnForDistance(distanceFt: number): number | null {
  if (!Number.isFinite(distanceFt)) return null;
  if (distanceFt < LINE_FOUR_MIN_FT || distanceFt > LINE_FOUR_MAX_FT) return null;
  if (distanceFt === LINE_FOUR_MAX_FT) return LINE_FOUR_COLUMNS - 1;
  const column = Math.floor((distanceFt - LINE_FOUR_MIN_FT) / LINE_FOUR_BAND_FT);
  return column >= 0 && column < LINE_FOUR_COLUMNS ? column : null;
}

export function lineFourColumnLabel(column: number): LineFourColumnLabel | null {
  return LINE_FOUR_COLUMN_LABELS[column] ?? null;
}

/** Lowest open row in a column, or null when the column is full/invalid. */
export function lowestOpenLineFourRow(
  board: LineFourBoard,
  column: number
): number | null {
  if (!Number.isInteger(column) || column < 0 || column >= LINE_FOUR_COLUMNS) return null;
  for (let row = 0; row < LINE_FOUR_ROWS; row++) {
    if (board[row]?.[column] === null) return row;
  }
  return null;
}

function cellKey(cell: LineFourCell): string {
  return `${cell.row}:${cell.column}`;
}

/**
 * Find all winning cells through the newly placed piece. Returning the whole
 * contiguous line (rather than an arbitrary four) makes 5+ runs and crossing
 * double-wins highlight correctly.
 */
export function winningLineThrough(
  board: LineFourBoard,
  row: number,
  column: number,
  playerIndex: LineFourPlayer
): LineFourCell[] {
  const directions: Array<[number, number]> = [
    [0, 1],
    [1, 0],
    [1, 1],
    [1, -1],
  ];
  const winning = new Map<string, LineFourCell>();

  for (const [dr, dc] of directions) {
    const line: LineFourCell[] = [{ row, column }];
    for (const sign of [-1, 1] as const) {
      let r = row + dr * sign;
      let c = column + dc * sign;
      while (
        r >= 0 &&
        r < LINE_FOUR_ROWS &&
        c >= 0 &&
        c < LINE_FOUR_COLUMNS &&
        board[r]?.[c] === playerIndex
      ) {
        line.push({ row: r, column: c });
        r += dr * sign;
        c += dc * sign;
      }
    }
    if (line.length >= 4) {
      for (const cell of line) winning.set(cellKey(cell), cell);
    }
  }

  return [...winning.values()].sort((a, b) => a.row - b.row || a.column - b.column);
}

/** Apply one measured rollout without mutating the prior state. */
/**
 * Drop a piece using the putt's ROLLED DISTANCE, the original mode.
 *
 * Thin wrapper over `applyLineFourColumn`, which is what the start-line variant
 * uses with a column resolved from the putt's angle instead. Only the choice of
 * column differs between the two modes — the board, the drop and the
 * four-in-a-row are identical, and keeping one implementation is what stops
 * them drifting apart (Cole, 2026-08-23).
 */
export function applyLineFourPutt(
  state: LineFourState,
  distanceFt: number
): AppliedLineFourPutt {
  return applyLineFourColumn(state, lineFourColumnForDistance(distanceFt));
}

/**
 * Drop a piece into an already-resolved column. `null` is a spent turn — the
 * putt did not qualify for any column.
 */
export function applyLineFourColumn(
  state: LineFourState,
  column: number | null
): AppliedLineFourPutt {
  if (state.winnerIndex !== null || state.isDraw) {
    return { state, verdict: { kind: 'ignored' } };
  }

  const playerIndex = state.turnIndex;
  const nextPlayerIndex = (1 - playerIndex) as LineFourPlayer;

  if (column === null) {
    return {
      state: {
        ...state,
        turnIndex: nextPlayerIndex,
        attemptCount: state.attemptCount + 1,
      },
      verdict: { kind: 'miss', playerIndex },
    };
  }

  const row = lowestOpenLineFourRow(state.board, column);
  if (row === null) {
    return {
      state: {
        ...state,
        turnIndex: nextPlayerIndex,
        attemptCount: state.attemptCount + 1,
      },
      verdict: { kind: 'full', playerIndex, column },
    };
  }

  const board = state.board.map((cells) => [...cells]);
  board[row][column] = playerIndex;
  const winningCells = winningLineThrough(board, row, column, playerIndex);
  const won = winningCells.length >= 4;
  const moveCount = state.moveCount + 1;
  const isDraw = !won && moveCount === LINE_FOUR_ROWS * LINE_FOUR_COLUMNS;

  return {
    state: {
      ...state,
      board,
      turnIndex: won || isDraw ? playerIndex : nextPlayerIndex,
      moveCount,
      attemptCount: state.attemptCount + 1,
      winnerIndex: won ? playerIndex : null,
      winningCells,
      isDraw,
    },
    verdict: { kind: 'placed', playerIndex, column, row, won },
  };
}

